import {CloudProvidersService} from "app/services/domain/cloud-providers.service";
import * as Models from "app/services/proxy/model/models";
import {Lookup, UtilsService} from "framework/services/utils.service";
import {BadgeColor} from "framework/ui/badge/badge.component";

export enum ConfigScope
{
    CloudAccount  = "cloud_account",
    Tenant        = "tenant",
    AccountMaster = "account_master",
    SystemDefined = "system_defined"
}

export interface AccountConfigGroup
{
    accountIds: string[];
    params: Lookup<any>;
}

export class PolicyUtilsService
{
    private static readonly ENGINE_LABELS: Lookup<string, Models.PolicyEngineType> = {
        [Models.PolicyEngineType.aws_config]             : "AWS Config",
        [Models.PolicyEngineType.aws_organization_policy]: "AWS Organization Policy",
        [Models.PolicyEngineType.azure_policy]           : "Azure Policy",
        [Models.PolicyEngineType.corestack_policy]       : "CoreStack Policy",
        [Models.PolicyEngineType.gcp_organization_policy]: "GCP Organization Policy",
        [Models.PolicyEngineType.gcp_policy]             : "GCP Policy",
        [Models.PolicyEngineType.kubernetes_policy]      : "Kubernetes Policy",
        [Models.PolicyEngineType.oracle_cloud_guard]     : "Oracle Cloud Guard"
    };

    static readonly POLICY_SUPPORTED_PROVIDERS: Models.PolicyServiceName[] = [
        Models.PolicyServiceName.AWS,
        Models.PolicyServiceName.Azure,
        Models.PolicyServiceName.GCP,
        Models.PolicyServiceName.OCI
    ];

    private static readonly ENGINE_PROVIDERS: Lookup<Models.PolicyServiceName[], Models.PolicyEngineType> = {
        [Models.PolicyEngineType.aws_config]             : [Models.PolicyServiceName.AWS],
        [Models.PolicyEngineType.aws_organization_policy]: [Models.PolicyServiceName.AWS],
        [Models.PolicyEngineType.azure_policy]           : [Models.PolicyServiceName.Azure],
        [Models.PolicyEngineType.gcp_organization_policy]: [Models.PolicyServiceName.GCP],
        [Models.PolicyEngineType.gcp_policy]             : [Models.PolicyServiceName.GCP],
        [Models.PolicyEngineType.oracle_cloud_guard]     : [Models.PolicyServiceName.OCI],
        [Models.PolicyEngineType.corestack_policy]       : PolicyUtilsService.POLICY_SUPPORTED_PROVIDERS,
        [Models.PolicyEngineType.kubernetes_policy]      : PolicyUtilsService.POLICY_SUPPORTED_PROVIDERS
    };

    private static readonly SCOPE_LABELS: Lookup<string, ConfigScope> = {
        [ConfigScope.CloudAccount] : "Cloud Account",
        [ConfigScope.Tenant]       : "Tenant",
        [ConfigScope.AccountMaster]: "Account Master",
        [ConfigScope.SystemDefined]: "System Defined"
    };

    static formatEngineLabel(engine: Models.PolicyEngineType | string): string
    {
        return PolicyUtilsService.ENGINE_LABELS[engine as Models.PolicyEngineType]
               || UtilsService.capitalizeFirstLetterAllWords(UtilsService.replaceUnderscoresWithSpaces(engine))
               || "";
    }

    static scopeLabel(scope: ConfigScope): string
    {
        return PolicyUtilsService.SCOPE_LABELS[scope] || "";
    }

    static providerIcon(provider: string): string | null
    {
        if (!provider)
        {
            return null;
        }
        return CloudProvidersService.getServiceProviderSvg(provider as Models.CloudServiceName);
    }

    static engineTypesForProviders(providers: string[]): Models.PolicyEngineType[]
    {
        const allEngines = UtilsService.getEnumValues<Models.PolicyEngineType>(Models.PolicyEngineType);
        if (!providers?.length)
        {
            return allEngines;
        }
        return allEngines.filter(engine => PolicyUtilsService.ENGINE_PROVIDERS[engine]?.some(p => providers.includes(p)));
    }

    static getSubClassificationEnum(classification: Models.PolicyClassification): object | null
    {
        switch (classification)
        {
            case Models.PolicyClassification.Cost:
                return Models.PolicyCostSubClassification;

            case Models.PolicyClassification.Security:
                return Models.PolicySecuritySubClassification;

            case Models.PolicyClassification.Operation:
                return Models.PolicyOperationSubClassification;

            case Models.PolicyClassification.Performance:
                return Models.PolicyPerformanceSubClassification;

            default:
                return null;
        }
    }

    static subClassificationsForClassifications(classifications: Models.PolicyClassification[]): Models.PolicySubClassification[]
    {
        if (!classifications?.length)
        {
            return UtilsService.getEnumValues<Models.PolicySubClassification>(Models.PolicySubClassification);
        }

        const subClassifications = new Set<Models.PolicySubClassification>();
        for (const classification of classifications)
        {
            const subEnum = PolicyUtilsService.getSubClassificationEnum(classification);
            if (!subEnum)
            {
                continue;
            }
            for (const value of UtilsService.getEnumValues<Models.PolicySubClassification>(subEnum))
            {
                subClassifications.add(value);
            }
        }
        return [...subClassifications];
    }

    static formatParamValue(value: any): string
    {
        if (value === null || value === undefined || value === "")
        {
            return "NA";
        }
        if (Array.isArray(value))
        {
            return value.length ? value.join(", ") : "NA";
        }
        if (typeof value === "object")
        {
            return JSON.stringify(value);
        }
        if (typeof value == "string")
        {
            return value;
        }
        return String(value);
    }

    static parseDefaultValue(value: string): any
    {
        try
        {
            return JSON.parse(value);
        }
        catch
        {
            return value;
        }
    }

    static encodeParamValue(value: any): string
    {
        const type = typeof value;
        if (type === "string")
        {
            return value;
        }
        if (type === "number" || type === "boolean")
        {
            return String(value);
        }
        return JSON.stringify(value);
    }

    static encodeParamValueAsJson(value: any): string
    {
        return JSON.stringify(value);
    }

    static buildParamsMap(parameters: Models.NamedParameter[]): Lookup<any>
    {
        const params: Lookup<any> = {};
        for (const parameter of parameters || [])
        {
            const raw              = parameter.schema?.default_value;
            params[parameter.name] = raw == null ? raw : PolicyUtilsService.parseDefaultValue(raw);
        }
        return params;
    }

    static groupAccountConfigs(results: { [key: string]: Models.PolicyParameterConfiguration }): AccountConfigGroup[]
    {
        const groups: AccountConfigGroup[] = [];
        const groupByKey                   = new Map<string, AccountConfigGroup>();

        for (const accountId of Object.keys(results || {}))
        {
            const params = PolicyUtilsService.buildParamsMap(results[accountId]?.parameters_configuration || []);
            const key    = PolicyUtilsService.canonicalParamsKey(params);
            const group  = groupByKey.get(key);
            if (group)
            {
                group.accountIds.push(accountId);
            }
            else
            {
                const created = {
                    accountIds: [accountId],
                    params
                };
                groupByKey.set(key, created);
                groups.push(created);
            }
        }
        return groups;
    }

    static normalizeParametersJson(value: string): string
    {
        let parsed: any;
        try
        {
            parsed = JSON.parse(value || "{}");
        }
        catch
        {
            return value ?? "";
        }
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
        {
            return JSON.stringify(parsed);
        }
        return PolicyUtilsService.canonicalParamsKey(parsed);
    }

    private static canonicalParamsKey(params: Lookup<any>): string
    {
        const sortedEntries = Object.keys(params)
                                    .sort()
                                    .map(name => [
                                        name,
                                        params[name]
                                    ]);
        return JSON.stringify(sortedEntries);
    }
}
